import { useEffect, type ComponentType } from "react";
import { useStytchMember, useStytchMemberSession } from "@stytch/react/b2b";
import {
  Bell as BellIcon,
  Menu as Bars3Icon,
  Home as HomeIcon,
  Activity as ActivityIcon,
  Wheat as GrainIcon,
  FlaskConical as FlaskConicalIcon,
  LineChart as LineChartIcon,
  X as XMarkIcon,
  Upload as UploadIcon,
  BarChartBig as BarChartBigIcon,
  Zap as ZapIcon,
} from "lucide-react";
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import AppPanel from "./components/ui/AppPanel";
import { DefaultStytchLogin } from "./section/StytchLogin";
import { TicketDashboard } from "./section/tickets/Dashboard";
import { DormanStagedDashboard } from "./section/dorman/DormanStagedDashboard";
import { SystemDashboard } from "./section/developer/SystemDashboard";
import { QueuesDashboard } from "./section/developer/QueuesDashboard";
import { DeveloperDashboardPage } from "./section/developer/Dashboard";
import { ClientNotificationsDashboard } from "./section/developer/Notification";
import { DeveloperTestPage } from "./section/developer/TestPage";
import { TicketUpload } from "./section/tickets/TicketUpload";
import { SalesAndPurchaseReports } from "./section/tickets/SalesAndPurchaseReports";
import { CogsReports } from "./section/tickets/CogsReports";
import { SpreadReport } from "./section/reports/SpreadReport";
import { InvoiceAndStatementReport } from "./section/reports/InvoiceAndStatementReport";
import { ContractQuantityDashboard } from "./section/alerts/ContractQuantityDashboard";
import { LocationDashboard } from "./section/general/LocationDashboard";
import { CostBasisDashboard } from "./section/general/CostBasisDashboard";
import { ContactsDashboard } from "./section/general/ContactsDashboard";
import { MobileClientUsersDashboard } from "./section/general/MobileClientUsersDashboard";
import { MobileResourcesDashboard } from "./section/general/MobileClientResourcesDashboard";
import { MarketZoneDashboard } from "./section/general/MarketZoneDashboard";
import { DestinationMessagingDashboard } from "./section/destination/DestinationMessagingDashboard";
import { DestinationMessagingConsentScreenshot } from "./section/destination/DestinationMessagingConsentScreenshot";
import { DestinationMessagingConsent } from "./section/destination/DestinationMessagingConsent";
import { GeneralContractsDashboard } from "./section/general/GeneralContractsDashboard";
import { GeneralContractSettingsDashboard } from "./section/general/GeneralContractSettingsDashboard";
import { StagedGeneralContractsDashboard } from "./section/general/StagedGeneralContractsDashboard";
import { AgtechContractsDashboard } from "./section/general/AgtechContractsDashboard";
import { ArReportDashboard } from "./section/reports/ArReportDashboard";
import { GFReportDashboard } from "./section/reports/GFReportDashboard";
import { OtcFeePremReportDashboard } from "./section/reports/OtcFeePremReportDashboard";
import { ContractMTMReportDashboard } from "./section/reports/ContractMTMReportDashboard";
import { DeferredPaymentDashboard } from "./section/payments/deferred/DeferredPaymentDashboard";
import { MarketingTransactionalReport } from "./section/reports/MarketingTransactionalReport";
import { QueuedDocumentsReport } from "./section/document/QueuedDocumentsReport";
import { TicketJobsDashboard } from "./section/tickets/TicketJobsDashboard";
import { TradingTicketDashboard } from "./section/tickets/TradingDashboard";
import { OtcDashboard } from "./section/otc/Dashboard";
import { PositionsDashboard } from "./section/otc/PositionsDashboard";
import { BacktestPage } from "./section/trading/backtest";
import { AtsDashboardPage } from "./section/trading/ats/dashboard";
import { AtsLivePage } from "./section/trading/ats/live";
import { RecommendationAnalysis } from "./section/AnalysisPortal/analysis";
import { RecommendationAlert } from "./section/AnalysisPortal/alert";
import { HedgeBeaconMarketThoughts } from "./section/AnalysisPortal/market_thoughts";
import { VideoEditorDashboard } from "./section/video/VideoEditorDashboard";
import { SplicingDashboard } from "./section/video/SplicingDashboard";
import { RoutePath, pagesList } from "./section/router";
import { ROUTE_RUNTIME_RULES } from "./section/routerPaths";
import { ManageCustomersPage } from "./section/customer/ManageCustomers";
import { ManageTradersPage } from "./section/trader/ManageTraders";
import { CallLogsDashboard } from "./section/callLogs/CallLogDashboard";
import { DeveloperJobsDashboard } from "./section/developer/JobsDashboard";
import { NewsArticlesDashboard } from "./section/news/NewsArticlesDashboard";
import { ChatDashboard } from "./section/chat/ChatDashboard";
import { GoToChatAuditDashboard } from "./section/gotoChat/GoToChatAuditDashboard";
import { ManagePermissionsPage } from "./section/permissions/ManagePermissions";
import { BetterAuthPermissionsPage } from "./section/permissions/BetterAuthPermissions";
import { BetterAuthPermissionsUserDetailPage } from "./section/permissions/BetterAuthPermissionsUserDetail";
import { MobilePermissionsLayout } from "./section/permissions/MobilePermissionsLayout";
import { ClientOrgTab } from "./section/permissions/ClientOrgTab";
import { FreightOrgTab } from "./section/permissions/FreightOrgTab";
import { EmailPreviewDashboard } from "./section/AnalysisPortal/email_preview";
import { KnowledgeTasksDashboard } from "./section/knowledge/KnowledgeTasksDashboard";
import { KnowledgeTaskDetail } from "./section/knowledge/KnowledgeTaskDetail";
import { KnowledgeTaskEdit } from "./section/knowledge/KnowledgeTaskEdit";
import { KnowledgeTaskCreate } from "./section/knowledge/KnowledgeTaskCreate";
import { PrivacyPolicyPage } from "./section/document/PrivacyPolicy";
import { ManualPriceData } from "./section/price-data/ManualPriceData";
import { DirectSalesDashboard } from "./section/direct-sales/DirectSalesDashboard";
import { SettingsDashboard } from "./section/settings";
import { GrainSamplesDashboard } from "./section/grainSamples";
import { GrainPositionDashboard } from "./section/grainPositions";
import { ExportSalesTrackerDashboard } from "./section/reports/ExportSalesTracking/Dashboard";
import { RiskExperimentsDashboard } from "./section/risk/RiskExperiments";
import { RiskExperimentsSummaryDashboard } from "./section/risk/RiskExperiments/SummaryDashboard";
import { RiskManagementDashboard } from "./section/risk/RiskManagementDashboard";
import { RiskManagementCropProfileDashboard } from "./section/risk/CropProfileDashboard";
import { RiskCashSalesDashboard } from "./section/risk/RiskCashSalesDashboard";
import { RiskManagementSummaryDashboard } from "./section/risk/RiskManagementSummaryDashboard";
import { RiskManagementFullDashboard } from "./section/risk/RiskManagementFullDashboard";
import { RiskManagementFullBrokerageDashboard } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/index";
import { RiskManagementRecommendationsDashobard } from "./section/risk/RiskManagementFullDashboard/Recommendation/Dashboard";
import { OpenOrdersTab } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/OpenOrdersTab";
import { RiskBrokeragePAndSTab } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/PAndSTab";
import { TTOrdersTab } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/TTOrdersTab";
import { TTDashboardTab, TTOrderDetailView } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/tt";
import { TtOrdersV2Tab } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/ttv2";
import { TtActiveOrdersTab } from "./section/risk/RiskManagementFullDashboard/BrokerageDashboard/ttv2-active";
import { RiskManagementSalesNotes } from "./section/risk/RiskSales/Dashboard";
import { RiskMarketZoneDashboard } from "./section/risk/RiskManagementFullDashboard/RiskMarketZoneDashboard";
import { MerchValuesDashboard } from "./section/risk/MerchValues/MerchValuesDashboard";
import { MerchValuesActiveTab } from "./section/risk/MerchValues/MerchValuesActiveTab";
import { MerchValuesHistoryTab } from "./section/risk/MerchValues/MerchValuesHistoryTab";
import { DestinationHoursDashboard } from "./section/destination/DestinationHoursDashboard";
import { DestinationHoursImprovedDashboard } from "./section/destination/DestinationHoursImprovedDashboard";
import { BirthdayFundDashboard } from "./section/fund/BirthdayFund";
import { BirthdayFundRequestsDashboard } from "./section/fund/BirthdayFund/BirthdayFundRequestsDashboard";
import { BirthdayFundCertificatesDashboard } from "./section/fund/BirthdayFund/BirthdayFundCertificatesDashboard";
import { BirthdayFundCertificateList } from "./section/fund/BirthdayFund/BirthdayFundCertificateList";
import { BirthdayFundCreateCertificate } from "./section/fund/BirthdayFund/BirthdayFundCreateCertificate";
import { BirthdayFundCreateInstance } from "./section/fund/BirthdayFund/BirthdayFundCreateInstance";
import { BeaconFundDashboard } from "./section/fund/BeaconFund";
import { TaskChecklistsDashboard } from "./section/task/checklists/TaskChecklistsDashboard";
import { BrokerageDashboard } from "./section/brokerage";
import { BrokerageFilesDashboard } from "./section/brokerage/BrokerageFilesDashboard";
import { BrokerageReportSettingsDashboard } from "./section/brokerage/BrokerageReportSettingsDashboard";
import { CfrDocumentsDashboard } from "./section/cfr/CfrDocumentsDashboard";
import { CfrReportsPage } from "./section/cfr/pages/CfrReportsPage";
import { CfrTemplatesPage } from "./section/cfr/pages/CfrTemplatesPage";
import { CfrInstancesPage } from "./section/cfr/pages/CfrInstancesPage";
import { CfrTemplateCreatePage } from "./section/cfr/pages/CfrTemplateCreatePage";
import {
  CfrInstanceFinalPreviewPage,
  CfrInstanceSentPreviewPage,
} from "./section/cfr/pages/CfrInstancePreviewPage";
import { ViewPdfPreview } from "./section/cfr/components/ViewPdfPreview";
import { AchDocumentsDashboard } from "./section/ach/AchDocumentsDashboard";
import { AchTemplatesPage } from "./section/ach/pages/AchTemplatesPage";
import { AchTemplateCreatePage } from "./section/ach/pages/AchTemplateCreatePage";
import { AchReviewInstancesPage } from "./section/ach/pages/AchReviewInstancesPage";
import {
  AchInstanceFinalPreviewPage,
  AchInstanceSentPreviewPage,
} from "./section/ach/pages/AchInstancePreviewPage";
import { ViewAchPdfPreview } from "./section/ach/components/ViewAchPdfPreview";
import { DestinationPacketsDashboard } from "./section/destinationPackets/DestinationPacketsDashboard";
import { DestinationPacketsTemplatesPage } from "./section/destinationPackets/pages/DestinationPacketsTemplatesPage";
import { DestinationPacketsTemplateCreatePage } from "./section/destinationPackets/pages/DestinationPacketsTemplateCreatePage";
import { DestinationPacketsTemplateViewPage } from "./section/destinationPackets/pages/DestinationPacketsTemplateViewPage";
import { DestinationPacketsTemplatePreviewPage } from "./section/destinationPackets/pages/DestinationPacketsTemplatePreviewPage";
import { DestinationPacketsSendEnvelopePage } from "./section/destinationPackets/pages/DestinationPacketsSendEnvelopePage";
import { DestinationPacketsInstancesPage } from "./section/destinationPackets/pages/DestinationPacketsInstancesPage";
import {
  DestinationPacketsInstanceFinalPreviewPage,
  DestinationPacketsInstanceSentPreviewPage,
} from "./section/destinationPackets/pages/DestinationPacketsInstancePreviewPage";
import { ContractsDashboard } from "./section/contracts/ContractsDashboard";
import { ContractsGeneralDashboard } from "./section/contracts/ContractsGeneralDashboard";
import { ContractsOtcDashboard } from "./section/contracts/ContractsOtcDashboard";
import { ContractsLanguageGeneralDashboard } from "./section/contracts/ContractsLanguageGeneralDashboard";
import { ContractsLanguageOtcDashboard } from "./section/contracts/ContractsLanguageOtcDashboard";
import { ContractsLanguageDashboard } from "./section/contracts/ContractsLanguageDashboard";
import { McCluskyFeeAccrualDashboard } from "./section/reports/McCluskyFeeAccrualDashboard";
import MarketSimilarityPage from "./section/marketSimilarity/MarketSimilarityPage";
import AssistantDashboard from "./section/assistant/AssistantDashboard";
import { AgtechDashboard } from "./section/agtech/AgtechDashboard";
import { AgtechReportsPage } from "./section/agtech/pages/AgtechReportsPage";
import { AgtechTicketsPage } from "./section/agtech/pages/AgtechTicketsPage";
import { AgtechSettlementsPage } from "./section/agtech/pages/AgtechSettlementsPage";
import { AgtechAdvancesPage } from "./section/agtech/pages/AgtechAdvancesPage";
import { FreightDashboard } from "./section/freight/FreightDashboard";
import { FreightImagesDashboard } from "./section/freight/FreightImagesDashboard";
import { DataTableBasicPage } from "./section/experiments/DataTableBasicPage";
import { DataTableColoredRowsPage } from "./section/experiments/DataTableColoredRowsPage";
import { TestOtcFormV2Page } from "./section/otc/contract-form-v2/TestPage";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from "./components/ui/navigation-menu";
import { cn } from "./lib/utils";
import { PossiblePackage, doesUserHavePackages } from "./lib/models/auth";

// TODO Log out when api calls returns unauthenticated because of expired tokens
// TODO add toasts and messages for login pages to tell the user why.
// Expired token etc
function ProtectedRoute({
  children,
  requiredPackages,
}: {
  children: JSX.Element;
  requiredPackages?: PossiblePackage[];
}): JSX.Element | null {
  const { session } = useStytchMemberSession();
  const { member } = useStytchMember();
  const navigate = useNavigate();

  // NOTE: can check session.authentication_factors.length < 2
  // for checking that 2FA exists
  useEffect(() => {
    if (!session) {
      // Save the current path before redirecting to login
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/') {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
      }
      navigate("/login");
    }

    if (
      session &&
      member &&
      !doesUserHavePackages({ requiredPackages, member })
    ) {
      navigate("/");
    }
  }, [session, member, requiredPackages]);

  if (!session) {
    return null;
  }

  if (!!member && !doesUserHavePackages({ requiredPackages, member })) {
    return <MissingPermissionsPage />;
  }

  return children;
}

function MissingPermissionsPage() {
  return (
    <div>
      <h1>Insufficient priviledges to access this page</h1>
      <p>
        Please contact your administrator if you believe you should have access
        to this page.
      </p>
    </div>
  );
}

function Dashboard() {
  const { member } = useStytchMember();
  const items = pagesList({ member, filterByPackages: true }).filter(
    (item) => !item.excludeFromDashboard && item.href !== RoutePath.Dashboard,
  );

  return (
    <div>
      <div className="container flex flex-col mb-2">
        <h2 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Dashboard
        </h2>
        <ul
          role="list"
          className="mt-6 grid grid-cols-1 gap-6 border-b border-t border-gray-200 py-6 sm:grid-cols-2"
        >
          {items.map((item, itemIdx) => (
            <li key={itemIdx} className="flow-root">
              <div className="relative -m-2 flex items-center space-x-4 rounded-xl p-2 focus-within:ring-2 focus-within:ring-indigo-500 hover:bg-gray-50">
                <div
                  className={cn(
                    item.background,
                    "flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg",
                  )}
                >
                  <item.icon
                    className="h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    <a href={item.href} className="focus:outline-none">
                      <span className="absolute inset-0" aria-hidden="true" />
                      <span>{item.name}</span>
                      <span aria-hidden="true"> &rarr;</span>
                    </a>
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {item.description}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

type PageElement = {
  path: RoutePath;
  element: ComponentType;
  children?: PageElement[];
};

function isLocalhostRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

const pageElements: PageElement[] = [
  {
    path: RoutePath.McCluskyFeeAccrual,
    element: McCluskyFeeAccrualDashboard,
  },
  {
    path: RoutePath.DestinationHoursDashboard,
    element: DestinationHoursDashboard,
  },
  {
    path: RoutePath.DestinationHoursImprovedDashboard,
    element: DestinationHoursImprovedDashboard,
  },
  {
    path: RoutePath.Settings,
    element: SettingsDashboard,
  },
  {
    path: RoutePath.DirectSales,
    element: DirectSalesDashboard,
  },
  {
    path: RoutePath.KnowledgeTasks,
    element: KnowledgeTasksDashboard,
  },
  {
    path: RoutePath.KnowledgeTaskDetail,
    element: KnowledgeTaskDetail,
  },
  {
    path: RoutePath.KnowledgeTaskEdit,
    element: KnowledgeTaskEdit,
  },
  {
    path: RoutePath.ManualPriceData,
    element: ManualPriceData,
  },
  {
    path: RoutePath.KnowledgeTaskCreate,
    element: KnowledgeTaskCreate,
  },
  {
    path: RoutePath.PrivacyPolicy,
    element: PrivacyPolicyPage,
  },
  {
    path: RoutePath.MobileClientUsers,
    element: MobileClientUsersDashboard,
  },
  {
    path: RoutePath.MobileResources,
    element: MobileResourcesDashboard,
  },
  {
    path: RoutePath.ManagePermissions,
    element: ManagePermissionsPage,
  },
  {
    path: RoutePath.NewsArticles,
    element: NewsArticlesDashboard,
  },
  {
    path: RoutePath.Chat,
    element: ChatDashboard
  },
  {
    path: RoutePath.GoToChat,
    element: GoToChatAuditDashboard,
  },
  {
    path: RoutePath.MarketSimilarity,
    element: MarketSimilarityPage,
  },
  {
    path: RoutePath.Assistant,
    element: AssistantDashboard,
  },
  {
    path: RoutePath.Dashboard,
    element: Dashboard,
  },
  {
    path: RoutePath.ARReport,
    element: ArReportDashboard,
  },
  {
    path: RoutePath.GFReport,
    element: GFReportDashboard,
  },
  {
    path: RoutePath.OtcPrem,
    element: OtcFeePremReportDashboard,
  },
  {
    path: RoutePath.ContractMTM,
    element: ContractMTMReportDashboard,
  },
  {
    path: RoutePath.HedgeBeaconAnalysis,
    element: RecommendationAnalysis,
  },
  {
    path: RoutePath.HedgeBeaconEmailPreview,
    element: EmailPreviewDashboard,
  },
  {
    path: RoutePath.HedgeBeaconAlert,
    element: RecommendationAlert,
  },
  {
    path: RoutePath.HedgeBeaconMarketThoughts,
    element: HedgeBeaconMarketThoughts,
  },
  {
    path: RoutePath.VideoDashboard,
    element: VideoEditorDashboard,
  },
  {
    path: RoutePath.HedgeBeaconSplicing,
    element: SplicingDashboard,
  },

  {
    path: RoutePath.CallLogsDashboard,
    element: CallLogsDashboard,
  },
  {
    path: RoutePath.ContractQuantityAlertDashboard,
    element: ContractQuantityDashboard,
  },
  {
    path: RoutePath.TicketsUpload,
    element: TicketUpload,
  },
  {
    path: RoutePath.SalesAndPurchaseReports,
    element: SalesAndPurchaseReports,
  },
  {
    path: RoutePath.CogsReports,
    element: CogsReports,
  },
  {
    path: RoutePath.SpreadReport,
    element: SpreadReport,
  },
  {
    path: RoutePath.MarketingTransactionalReport,
    element: MarketingTransactionalReport,
  },
  {
    path: RoutePath.QueuedDocuments,
    element: QueuedDocumentsReport,
  },
  {
    path: RoutePath.TicketsWeightDashboard,
    element: TicketDashboard,
  },
  {
    path: RoutePath.TicketsJobsDashboard,
    element: TicketJobsDashboard,
  },
  {
    path: RoutePath.ManageCustomers,
    element: ManageCustomersPage,
  },
  {
    path: RoutePath.ManageTraders,
    element: ManageTradersPage,
  },
  {
    path: RoutePath.TicketsTradingDashboard,
    element: TradingTicketDashboard,
  },
  {
    path: RoutePath.OtcDashboard,
    element: OtcDashboard,
  },
  {
    path: RoutePath.LocationDashboard,
    element: LocationDashboard,
  },
  {
    path: RoutePath.CostBasisDashboard,
    element: CostBasisDashboard,
  },
  {
    path: RoutePath.ContactsDashboard,
    element: ContactsDashboard,
  },
  {
    path: RoutePath.MarketZoneDashboard,
    element: MarketZoneDashboard,
  },
  {
    path: RoutePath.DestinationMessagingDashboard,
    element: DestinationMessagingDashboard,
  },
  {
    path: RoutePath.DestinationMessagingConsentScreenshot,
    element: DestinationMessagingConsentScreenshot,
  },
  {
    path: RoutePath.DestinationMessagingConsent,
    element: DestinationMessagingConsent,
  },
  {
    path: RoutePath.GeneralContractsDashboard,
    element: GeneralContractsDashboard,
  },
  {
    path: RoutePath.GeneralContractSettingsDashboard,
    element: GeneralContractSettingsDashboard,
  },
  {
    path: RoutePath.StagedGeneralContractsDashboard,
    element: StagedGeneralContractsDashboard,
  },
  {
    path: RoutePath.AgtechContractsDashboard,
    element: AgtechContractsDashboard,
  },
  {
    path: RoutePath.PositionsDashboard,
    element: PositionsDashboard,
  },
  {
    path: RoutePath.TradingAtsLive,
    element: AtsLivePage,
  },
  {
    path: RoutePath.TradingBacktest,
    element: BacktestPage,
  },
  {
    path: RoutePath.TradingAtsData,
    element: AtsDashboardPage,
  },
  {
    path: RoutePath.DeveloperDashboard,
    element: DeveloperDashboardPage,
  },
  {
    path: RoutePath.System,
    element: SystemDashboard,
    children: [
      {
        path: RoutePath.SystemQueues,
        element: QueuesDashboard,
      },
    ],
  },
  {
    path: RoutePath.DeveloperTestPage,
    element: DeveloperTestPage,
  },
  {
    path: RoutePath.RiskExperiments,
    element: RiskExperimentsDashboard,
    children: [
      {
        path: RoutePath.RiskExperimentsSummary,
        element: RiskExperimentsSummaryDashboard,
      },
    ],
  },
  {
    path: RoutePath.DeveloperClientNotifications,
    element: ClientNotificationsDashboard,
  },
  {
    path: RoutePath.DormanStagedDashboard,
    element: DormanStagedDashboard,
  },
  {
    path: RoutePath.Login,
    element: DefaultStytchLogin,
  },
  {
    path: RoutePath.Authenticate,
    element: DefaultStytchLogin,
  },
  {
    path: RoutePath.DeveloperJobsDashboard,
    element: DeveloperJobsDashboard,
  },
  {
    path: RoutePath.InvoiceAndStatementReport,
    element: InvoiceAndStatementReport,
  },
  {
    path: RoutePath.DeferredPayments,
    element: DeferredPaymentDashboard,
  },
  {
    path: RoutePath.GrainSamplesDashboard,
    element: GrainSamplesDashboard,
  },
  {
    path: RoutePath.GrainPositionsDashboard,
    element: GrainPositionDashboard,
  },
  {
    path: RoutePath.RiskManagementSummary,
    element: RiskManagementSummaryDashboard,
  },
  {
    path: RoutePath.RiskManagement,
    element: RiskManagementDashboard,
  },
  {
    path: RoutePath.RiskManagementCropProfiles,
    element: RiskManagementCropProfileDashboard,
  },
  {
    path: RoutePath.BirthdayFund,
    element: BirthdayFundDashboard,
    children: [
      {
        path: RoutePath.BirthdayFundRequests,
        element: BirthdayFundRequestsDashboard,
      },
      {
        path: RoutePath.BirthdayFundCertificates,
        element: BirthdayFundCertificatesDashboard,
      },
      {
        path: RoutePath.BirthdayFundCertificateList,
        element: BirthdayFundCertificateList,
      },
      {
        path: RoutePath.BirthdayFundCreateCertificate,
        element: BirthdayFundCreateCertificate,
      },
      {
        path: RoutePath.BirthdayFundCreateInstance,
        element: BirthdayFundCreateInstance,
      }
    ]
  },
  {
    path: RoutePath.BeaconFund,
    element: BeaconFundDashboard,
  },
  {
    path: RoutePath.RiskManagementCashSales,
    element: RiskCashSalesDashboard
  },
  {
    path: RoutePath.RiskManagementFull,
    element: RiskManagementFullDashboard,
  },
  {
    path: RoutePath.RiskManagementSalesNotes,
    element: RiskManagementSalesNotes,
  },
  {
    path: RoutePath.ContractsDashboard,
    element: ContractsDashboard,
    children: [
      {
        path: RoutePath.ContractsGeneralDashboard,
        element: ContractsGeneralDashboard,
      },
      {
        path: RoutePath.ContractsOtcDashboard,
        element: ContractsOtcDashboard,
      },
      {
        path: RoutePath.ContractsLanguageGeneralDashboard,
        element: ContractsLanguageGeneralDashboard,
      },
      {
        path: RoutePath.ContractsLanguageOtcDashboard,
        element: ContractsLanguageOtcDashboard,
      },
      {
        path: RoutePath.ContractsLanguageDashboard,
        element: ContractsLanguageDashboard,
      },
    ]
  },
  {
    path: RoutePath.RiskManagementFullBrokerage,
    element: RiskManagementFullBrokerageDashboard,
    children: [
      {
        path: RoutePath.RiskManagementFullBrokerageOpenOrders,
        element: OpenOrdersTab,
      },
      {
        path: RoutePath.RiskManagementFullBrokeragePAndS,
        element: RiskBrokeragePAndSTab,
      },
      {
        path: RoutePath.TTOrders,
        element: TTOrdersTab,
      },
      {
        path: RoutePath.TTOrdersDashboard,
        element: TTDashboardTab,
      },
      {
        path: RoutePath.TTOrdersDetail,
        element: TTOrderDetailView,
      },
      {
        path: RoutePath.TtOrdersV2Dashboard,
        element: TtOrdersV2Tab,
      },
      {
        path: RoutePath.TtActiveOrdersDashboard,
        element: TtActiveOrdersTab,
      },
    ]
  },
  {
    path: RoutePath.RiskManagementRecommendations,
    element: RiskManagementRecommendationsDashobard,
  },
  {
    path: RoutePath.RiskMarketZone,
    element: RiskMarketZoneDashboard,
  },
  {
    path: RoutePath.RiskMerchValues,
    element: MerchValuesDashboard,
    children: [
      {
        path: RoutePath.RiskMerchValuesActive,
        element: MerchValuesActiveTab,
      },
      {
        path: RoutePath.RiskMerchValuesHistory,
        element: MerchValuesHistoryTab,
      },
    ]
  },
  {
    path: RoutePath.TaskChecklists,
    element: TaskChecklistsDashboard,
  },
  {
    path: RoutePath.Brokerage,
    element: BrokerageDashboard,
    children: [
      {
        path: RoutePath.BrokerageFiles,
        element: BrokerageFilesDashboard,
      },
      {
        path: RoutePath.BrokerageReportSettings,
        element: BrokerageReportSettingsDashboard,
      },
    ],
  },
  {
    path: RoutePath.ExportSalesTracker,
    element: ExportSalesTrackerDashboard,
  },
  {
    path: RoutePath.CfrDocuments,
    element: CfrDocumentsDashboard,
    children: [
      {
        path: RoutePath.CfrDocumentsReports,
        element: CfrReportsPage,
      },
      {
        path: RoutePath.CfrDocumentsTemplates,
        element: CfrTemplatesPage,
      },
      {
        path: RoutePath.CfrDocumentsTemplatesView,
        element: ViewPdfPreview,
      },
      {
        path: RoutePath.CfrDocumentsTemplatesCreate,
        element: CfrTemplateCreatePage,
      },
      {
        path: RoutePath.CfrDocumentsInstanceSentPreview,
        element: CfrInstanceSentPreviewPage,
      },
      {
        path: RoutePath.CfrDocumentsInstanceFinalPreview,
        element: CfrInstanceFinalPreviewPage,
      },
      {
        path: RoutePath.CfrDocumentsInstances,
        element: CfrInstancesPage,
      },
    ],
  },
  {
    path: RoutePath.AchDocuments,
    element: AchDocumentsDashboard,
    children: [
      {
        path: RoutePath.AchDocumentsTemplates,
        element: AchTemplatesPage,
      },
      {
        path: RoutePath.AchDocumentsTemplatesCreate,
        element: AchTemplateCreatePage,
      },
      {
        path: RoutePath.AchDocumentsTemplatesView,
        element: ViewAchPdfPreview,
      },
      {
        path: RoutePath.AchDocumentsReviewInstances,
        element: AchReviewInstancesPage,
      },
      {
        path: RoutePath.AchDocumentsInstanceSentPreview,
        element: AchInstanceSentPreviewPage,
      },
      {
        path: RoutePath.AchDocumentsInstanceFinalPreview,
        element: AchInstanceFinalPreviewPage,
      },
    ],
  },
  {
    path: RoutePath.DestinationPackets,
    element: DestinationPacketsDashboard,
    children: [
      {
        path: RoutePath.DestinationPacketsTemplates,
        element: DestinationPacketsTemplatesPage,
      },
      {
        path: RoutePath.DestinationPacketsTemplatesCreate,
        element: DestinationPacketsTemplateCreatePage,
      },
      {
        path: RoutePath.DestinationPacketsTemplatesView,
        element: DestinationPacketsTemplateViewPage,
      },
      {
        path: RoutePath.DestinationPacketsTemplatesPreview,
        element: DestinationPacketsTemplatePreviewPage,
      },
      {
        path: RoutePath.DestinationPacketsSendEnvelope,
        element: DestinationPacketsSendEnvelopePage,
      },
      {
        path: RoutePath.DestinationPacketsInstances,
        element: DestinationPacketsInstancesPage,
      },
      {
        path: RoutePath.DestinationPacketsInstanceSentPreview,
        element: DestinationPacketsInstanceSentPreviewPage,
      },
      {
        path: RoutePath.DestinationPacketsInstanceFinalPreview,
        element: DestinationPacketsInstanceFinalPreviewPage,
      },
    ],
  },
  {
    path: RoutePath.ManagePermissions,
    element: ManagePermissionsPage,
  },
  {
    path: RoutePath.MobilePermissions,
    element: MobilePermissionsLayout,
    children: [
      {
        path: RoutePath.MobilePermissionsClient,
        element: ClientOrgTab,
      },
      {
        path: RoutePath.MobilePermissionsFreight,
        element: FreightOrgTab,
      },
    ],
  },
  {
    path: RoutePath.MobilePermissionsUserDetail,
    element: BetterAuthPermissionsUserDetailPage,
  },
  {
    path: RoutePath.Agtech,
    element: AgtechDashboard,
    children: [
      {
        path: RoutePath.AgtechReports,
        element: AgtechReportsPage,
      },
      {
        path: RoutePath.AgtechTickets,
        element: AgtechTicketsPage,
      },
      {
        path: RoutePath.AgtechSettlements,
        element: AgtechSettlementsPage,
      },
      {
        path: RoutePath.AgtechAdvances,
        element: AgtechAdvancesPage,
      },
    ],
  },
  {
    path: RoutePath.FreightDashboard,
    element: FreightDashboard,
    children: [
      {
        path: RoutePath.FreightImagesDashboard,
        element: FreightImagesDashboard,
      },
    ],
  },
  {
    path: RoutePath.ExperimentsDataTableBasic,
    element: DataTableBasicPage,
  },
  {
    path: RoutePath.ExperimentsDataTableColoredRows,
    element: DataTableColoredRowsPage,
  },
  {
    path: RoutePath.TestOtcFormV2,
    element: TestOtcFormV2Page,
  },

];

const router = createBrowserRouter(
  pageElements
    .filter((item) => {
      const runtimeRules = ROUTE_RUNTIME_RULES[item.path];
      return !(runtimeRules?.localhostOnly && !isLocalhostRuntime());
    })
    .map((item) => {
    const pageItem = pagesList({ filterByPackages: false }).find(
      (page) => page.href === item.path,
    );
    if (!pageItem) throw new Error("Failed to find item");

    const Element = item.element;

    // Helper function to wrap child routes
    const wrapChildRoute = (child: PageElement, parentPath: RoutePath) => {
      const childPageItem = pagesList({ filterByPackages: false }).find(
        (page) => page.href === child.path,
      );

      if (!childPageItem) {
        throw new Error(`Failed to find child item for path: ${child.path}`);
      }

      const ChildElement = child.element;

      // Extract relative path by removing parent prefix
      const relativePath = child.path.startsWith(parentPath + '/')
        ? child.path.slice((parentPath + '/').length)
        : child.path;


      if (childPageItem.isProtected) {
        return {
          path: relativePath,
          element: (
            <ProtectedRoute requiredPackages={childPageItem.requiredPackages}>
              <ChildElement />
            </ProtectedRoute>
          ),
        };
      }

      return {
        path: relativePath,
        element: (
          <UnAuthedPageFrame shouldStayOnUnAuthed={childPageItem.shouldStayOnUnAuthed}>
            <ChildElement />
          </UnAuthedPageFrame>
        ),
      };
    };

    // Process children if they exist
    const children = item.children
      ? item.children.map((child) => wrapChildRoute(child, item.path))
      : undefined;

    if (pageItem.isProtected) {
      return {
        path: item.path,
        element: (
          <ProtectedRoute requiredPackages={pageItem.requiredPackages}>
            <AppPanel>
              <Element />
            </AppPanel>
          </ProtectedRoute>
        ),
        children,
      };
    }

      return {
        path: item.path,
        element: (
          <UnAuthedPageFrame shouldStayOnUnAuthed={pageItem.shouldStayOnUnAuthed}>
            <Element />
          </UnAuthedPageFrame>
        ),
        children,
      };
    }),
);

function App() {
  // TODO add a router and check store for token to private protect routes
  return (
    <main className="flex-1 overflow-y-auto overflow-x-clip">
      <RouterProvider router={router} />
      <Toaster />
    </main>
  );
}

export default App;

// TODO investigate Stytch Refresh Session
// https://stytch.com/docs/b2b/sdks/javascript-sdk/session-management#refresh-session
export function UnAuthedPageFrame({
  children,
  shouldStayOnUnAuthed,
}: {
  children: JSX.Element;
  shouldStayOnUnAuthed?: boolean;
}) {
  // TODO convert protected and unauthed to handle stytch auth
  const { session } = useStytchMemberSession();
  const navigate = useNavigate();

  // NOTE: can check session.authentication_factors.length < 2
  // for checking that 2FA exists
  useEffect(() => {
    if (!shouldStayOnUnAuthed && session) {
      // Check if there's a saved redirect path in sessionStorage
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      
      // Also check for redirect parameter in URL (for magic link callbacks)
      const urlParams = new URLSearchParams(window.location.search);
      const urlRedirect = urlParams.get('redirect');
      
      // For magic link auth, Stytch might strip our custom params, so we rely on sessionStorage
      const finalRedirect = redirectPath || urlRedirect;
      
      if (finalRedirect) {
        // Clear the saved redirect path
        sessionStorage.removeItem('redirectAfterLogin');
        // Use replace instead of navigate to avoid adding to history
        navigate(finalRedirect, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [session, shouldStayOnUnAuthed]);

  if (!!session && !shouldStayOnUnAuthed) {
    return null;
  }

  return (
    <div className="flex flex-col items-stretch h-screen w-screen overflow-clip">
      <main className="flex-1 overflow-y-auto overflow-x-clip">
        <div className="flex items-center flex-col h-full">
          <div className="container h-full">{children}</div>
        </div>
      </main>
    </div>
  );
}
